import { useSelector } from 'react-redux'

import { Role, RoleExtended } from '@/features/managed-accounts'
import { selectRole } from '@/redux/me/meSlice'

export const usePermissions = () => {
  const role = useSelector(selectRole)

  // Roles
  const isPM = role === Role.PROGRAM_MANAGER
  const isImplementer = role === Role.IMPLEMENTER
  const isReadOnly = role === Role.READ_ONLY_USER
  const isAdmin = isImplementer || isPM || isReadOnly
  const isOperator = role === Role.OPERATOR

  // Page access
  const shouldShowDashboardPage = isPM || isReadOnly
  const shouldShowSOPPage = !isImplementer
  const shouldShowEditMode = isImplementer || isPM || isReadOnly
  const shouldShowSettingsPage = isImplementer || isPM || isReadOnly
  const shouldShowIncidentRulesPage = isPM || isOperator || isImplementer
  const shouldShowCaseManagementPage = isPM || isOperator
  const shouldShowOperatorHubPage = isImplementer

  // Incident actions
  const shouldEnableIncidentActions = !isReadOnly
  const shouldEnableIncidentRadioGuards = !isReadOnly
  const shouldEnableIncidentEmergencyCall = !isReadOnly
  const shouldEnableIncidentNote = !isReadOnly
  const shouldEnableSOPIncidentDrawerToggle = !isImplementer
  const shouldShowDownloadReportButton = isOperator || isPM
  const canBulkAction = isImplementer || isPM || isOperator

  //Modals
  const shouldEnableRemoveModal = !isReadOnly

  // Incident
  const shouldEnableCreateIncident = !isReadOnly
  const shouldShowCreateManualIncidentCategory = isImplementer || isPM

  // Device
  const shouldEnableCreateDevice = isImplementer
  const shouldEnableUpdateDevice = isImplementer || isPM
  const shouldEnableDeleteDevice = isImplementer
  const shouldShowExternalAccountFields = isImplementer
  // Associate Devices
  const shouldEnableAssociateDevices = isImplementer || isPM
  const shouldEnableConfigureTailgateGrid = isImplementer || isPM
  // Facility
  const shouldEnableCreateFacility = isImplementer
  const shouldEnableUpdateFacility = isImplementer || isPM
  const shouldEnableUpdateFacilityAddress = isImplementer
  const shouldEnableDeleteFacility = isImplementer
  // Floor
  const shouldEnableCreateFloor = isImplementer || isPM
  const shouldEnableUpdateFloor = isImplementer || isPM
  // Checkpoint
  const shouldEnableCreateCheckpoint = isImplementer || isPM
  const shouldEnableUpdateCheckpoint = isImplementer || isPM
  const shouldEnableDeleteCheckpoint = isImplementer || isPM
  const shouldEnableCheckpointChecklist = isPM
  // TourPlan
  const shouldEnableCreateTourPlan = isImplementer || isPM
  const shouldEnableUpdateTourPlan = isImplementer || isPM
  const shouldEnableDeleteTourPlan = isImplementer || isPM
  // SOP
  const shouldEnableCreateSOP = isPM
  const shouldEnableUpdateSOP = isPM
  // Incident Rules
  const shouldEnableCreateIncidentsRule = isPM || isImplementer
  const shouldShowMuteFilter = isImplementer
  const shouldShowClipsFilter = isImplementer
  const shouldOnlyShowAlarmed = !isImplementer
  // Gateway Devices
  const shouldEnableCreateGateway = isImplementer
  const shouldEnableUpdateGateway = isImplementer || isPM
  const shouldEnableRestartGateway = isImplementer
  // PACS
  const shouldEnableCreatePACS = isImplementer
  // User, Technician
  const shouldEnableCreateUser = isImplementer || isPM
  const shouldEnableUpdateUser = isImplementer || isPM
  const shouldEnableCreateTechnician = isImplementer || isPM
  const shouldEnableUpdateTechnician = isImplementer || isPM
  // Setting - DatabaseReset, DeviceHealth Setting, Organization Settings
  const shouldEnableResetDatabase = isPM
  const shouldEnableUpdateDeviceHealthSettings = isImplementer || isPM
  const shouldEnableUpdateOrganizationSettings = isImplementer || isPM
  const shouldShowPACSSourceSecretsSettings = isImplementer
  const shouldShowExternalAccountSettings = isImplementer

  //Case Management
  const shouldEnableCaseManagement = isPM || isOperator
  const shouldEnableCaseDownload = isPM

  //QuickReport
  const shouldShowQuickReport = isPM || isImplementer
  const shouldEnableCreateQuickReportCategory = isPM || isImplementer
  const shouldEnableWhitelistedUsersForIncidentCategory = isImplementer
  const shouldEditQuickReportCategory = isPM || isImplementer

  //Send to Ontic
  const shouldEnableSendToOntic = isPM || isOperator

  //Ai operator
  const shouldUpdateAiLimit = isImplementer

  /**
   * This checks if a user has permission to add/edit/delete the specified role
   * crudRole - the role to perform the CRUD operation on
   */
  const hasCRUDPermission = (crudRole: Role | RoleExtended) => {
    if (role === Role.IMPLEMENTER) {
      return true
    } else if (role === Role.PROGRAM_MANAGER && crudRole !== Role.IMPLEMENTER) {
      return true
    }
    return false
  }

  return {
    hasCRUDPermission,
    isAdmin,
    isImplementer,
    shouldEnableAssociateDevices,
    shouldEnableCaseDownload,
    shouldEnableCaseManagement,
    shouldEnableConfigureTailgateGrid,
    shouldEnableCreateCheckpoint,
    shouldEnableCreateDevice,
    shouldEnableCreateFacility,
    shouldEnableCreateFloor,
    shouldEnableCreateGateway,
    shouldEnableCreateIncident,
    shouldEnableCreateIncidentsRule,
    shouldShowMuteFilter,
    shouldShowClipsFilter,
    shouldOnlyShowAlarmed,
    shouldEnableCreatePACS,
    shouldEnableCreateSOP,
    shouldEnableCreateTechnician,
    shouldEnableCreateTourPlan,
    shouldEnableCreateUser,
    shouldEnableDeleteCheckpoint,
    shouldEnableDeleteDevice,
    shouldEnableDeleteFacility,
    shouldEnableDeleteTourPlan,
    shouldEnableIncidentActions,
    shouldEnableIncidentEmergencyCall,
    shouldEnableIncidentNote,
    shouldEnableIncidentRadioGuards,
    shouldEnableRemoveModal,
    shouldEnableResetDatabase,
    shouldEnableRestartGateway,
    shouldEnableSOPIncidentDrawerToggle,
    shouldEnableUpdateCheckpoint,
    shouldEnableUpdateDevice,
    shouldEnableUpdateDeviceHealthSettings,
    shouldEnableUpdateFacility,
    shouldEnableUpdateFacilityAddress,
    shouldEnableUpdateFloor,
    shouldEnableUpdateGateway,
    shouldEnableUpdateOrganizationSettings,
    shouldEnableUpdateSOP,
    shouldEnableUpdateTechnician,
    shouldEnableUpdateTourPlan,
    shouldEnableUpdateUser,
    shouldShowCreateManualIncidentCategory,
    shouldShowDashboardPage,
    shouldShowDownloadReportButton,
    canBulkAction,
    shouldShowEditMode,
    shouldShowIncidentRulesPage,
    shouldShowPACSSourceSecretsSettings,
    shouldShowSOPPage,
    shouldShowSettingsPage,
    shouldShowCaseManagementPage,
    shouldShowOperatorHubPage,
    shouldShowExternalAccountSettings,
    shouldShowExternalAccountFields,
    shouldShowQuickReport,
    shouldEnableSendToOntic,
    shouldEnableCreateQuickReportCategory,
    shouldEnableWhitelistedUsersForIncidentCategory,
    shouldEditQuickReportCategory,
    shouldEnableCheckpointChecklist,
    shouldUpdateAiLimit,
  }
}
